import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  type ReactNode,
} from 'react';
import type { ThemeId, ThemeContextValue } from '@/types/theme';
import { themes, getThemeById, isValidThemeId, VALID_THEME_IDS } from '@/lib/themes';

const ThemeContext = createContext<ThemeContextValue | null>(null);

const THEME_STORAGE_KEY = 'ml4-theme';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>(() => {
    if (typeof window === 'undefined') return 'dark';
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored && isValidThemeId(stored) ? stored : 'dark';
  });

  useEffect(() => {
    const root = document.documentElement;

    // Remove all theme classes
    VALID_THEME_IDS.forEach((t) => root.classList.remove(`theme-${t}`));

    // Add current theme class
    root.classList.add(`theme-${theme}`);

    // Also keep 'dark' class for backwards compatibility with any code using it
    if (getThemeById(theme).isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Persist to localStorage
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  const themeConfig = useMemo(() => getThemeById(theme), [theme]);

  const setTheme = (newTheme: ThemeId) => {
    if (isValidThemeId(newTheme)) {
      setThemeState(newTheme);
    }
  };

  // Convenience method for toggling between dark and light
  const toggleTheme = () => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  return (
    <ThemeContext.Provider
      value={{ theme, themeConfig, setTheme, themes, toggleTheme }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
